import { Upload } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { Button, Card, CardContent, CardHeader, CardTitle, Field, Select, Status, cn, type SelectOption } from '@buildbee/ui';
import { statusTone } from '../status-tone';
import { statusLabel, type Lookups } from './lib';

export function StatusBadge({ status, className }: { status: string; className?: string }) {
  return (
    <Status tone={statusTone(status)} className={className}>
      {statusLabel(status)}
    </Status>
  );
}

export function StatCard({ label, value, hint, tone }: { label: string; value: ReactNode; hint?: ReactNode; tone?: 'default' | 'warning' | 'destructive' | 'success' }) {
  return (
    <Card>
      <CardContent className="space-y-1 p-5">
        <p className="text-[13px] font-medium text-muted-foreground">{label}</p>
        <p
          className={cn(
            'text-2xl font-semibold figures',
            tone === 'warning' && 'text-warning',
            tone === 'destructive' && 'text-destructive',
            tone === 'success' && 'text-success',
          )}
        >
          {value}
        </p>
        {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      </CardContent>
    </Card>
  );
}

export function Panel({ title, actions, children, className }: { title: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <Card className={className}>
      <CardHeader className="flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle>{title}</CardTitle>
        {actions}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

/** Definition list row used in detail drawers. */
export function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-1.5 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="col-span-2 min-w-0 break-words">{children ?? '—'}</dd>
    </div>
  );
}

/** File picker button that calls `onFiles` with the chosen files. */
export function FilePickerButton({
  onFiles,
  accept,
  multiple,
  label = 'Upload',
  loading,
  disabled,
  variant = 'primary',
}: {
  onFiles: (files: File[]) => void | Promise<void>;
  accept?: string;
  multiple?: boolean;
  label?: string;
  loading?: boolean;
  disabled?: boolean;
  variant?: 'primary' | 'outline' | 'secondary';
}) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={ref}
        type="file"
        className="sr-only"
        accept={accept}
        multiple={multiple}
        aria-label={label}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = '';
          if (files.length) void onFiles(files);
        }}
      />
      <Button variant={variant} onClick={() => ref.current?.click()} loading={loading} disabled={disabled}>
        <Upload aria-hidden />
        {label}
      </Button>
    </>
  );
}

/** Drop zone that also opens the file picker on click. */
export function DropZone({ onFiles, accept, children, disabled }: { onFiles: (files: File[]) => void; accept?: string; children: ReactNode; disabled?: boolean }) {
  const [over, setOver] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  return (
    <div
      role="button"
      tabIndex={0}
      aria-disabled={disabled}
      onClick={() => !disabled && ref.current?.click()}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !disabled) ref.current?.click();
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        if (!disabled) onFiles(Array.from(e.dataTransfer.files));
      }}
      className={cn('flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border bg-card/50 p-6 text-center text-sm text-muted-foreground transition-colors', over && 'border-primary bg-accent', disabled && 'cursor-not-allowed opacity-60')}
    >
      <input
        ref={ref}
        type="file"
        multiple
        accept={accept}
        className="sr-only"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = '';
          if (files.length) onFiles(files);
        }}
      />
      {children}
    </div>
  );
}

export function userOptions(lookups: Lookups | undefined, scope: 'team' | 'workspace' = 'workspace'): SelectOption[] {
  const list = scope === 'team' || !lookups?.workspaceUsers.length ? (lookups?.team ?? []) : lookups.workspaceUsers;
  return list.map((u) => ({ value: u.userId, label: u.name }));
}

export function contactOptions(lookups: Lookups | undefined): SelectOption[] {
  return (lookups?.contacts ?? []).map((c) => ({ value: c.id, label: c.companyName ? `${c.name} (${c.companyName})` : c.name }));
}

export function companyOptions(lookups: Lookups | undefined, kinds?: string[]): SelectOption[] {
  return (lookups?.companies ?? [])
    .filter((c) => !kinds || c.relationshipTypes.some((r) => kinds.includes(r)))
    .map((c) => ({ value: c.id, label: c.trades.length ? `${c.name} — ${c.trades.join(', ')}` : c.name }));
}

export function drawingOptions(lookups: Lookups | undefined): SelectOption[] {
  return (lookups?.drawings ?? []).map((d) => ({ value: d.id, label: `${d.number} ${d.title}` }));
}

/** Uncontrolled-friendly select field for optional ids ('' means none). */
export function IdSelect({
  label,
  value,
  onChange,
  options,
  placeholder = 'None',
  id,
}: {
  label: string;
  value: string | null | undefined;
  onChange: (v: string | null) => void;
  options: SelectOption[];
  placeholder?: string;
  id?: string;
}) {
  return (
    <Field label={label} htmlFor={id}>
      <Select id={id} value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} options={[{ value: '', label: placeholder }, ...options]} />
    </Field>
  );
}

/** Searchable checkbox list for picking several people. */
export function MultiPick({ label, options, value, onChange, emptyText = 'Nobody to choose from' }: { label: string; options: SelectOption[]; value: string[]; onChange: (v: string[]) => void; emptyText?: string }) {
  const [q, setQ] = useState('');
  const shown = options.filter((o) => o.label.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium">
        {label} {value.length ? <span className="text-muted-foreground">({value.length})</span> : null}
      </legend>
      {options.length > 8 ? <input className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm" placeholder="Filter…" aria-label={`Filter ${label}`} value={q} onChange={(e) => setQ(e.target.value)} /> : null}
      <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg bg-muted/60 p-2">
        {shown.length === 0 ? <p className="text-xs text-muted-foreground">{emptyText}</p> : null}
        {shown.map((o) => (
          <label key={o.value} className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={value.includes(o.value)} onChange={(e) => onChange(e.target.checked ? [...value, o.value] : value.filter((v) => v !== o.value))} />
            <span className="truncate">{o.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
